import {GraphQLQuery} from '@aws-amplify/api';
import {generateClient} from "aws-amplify/api";
import {OrganizationAccountConnection} from '../../accounts/types';
import {listOrganizationAccounts} from './queries';

const createOrganizationAccountMutation = `
    mutation CreateOrganizationAccount($input: CreateOrganizationAccountInput!) {
        createOrganizationAccount(input: $input) {
            id
            organizationId
            accountId
            organizationAccountStatus
            created
        }
    }
`;

const listAccountsQuery = `
    query ListAccounts($filter: AccountsFilterInput) {
        listAccounts(filter: $filter) {
            items {
                id
                email
                username
                accountType
                accountStatus
            }
        }
    }
`;

const client = generateClient();

export const getAccountsByOrganization = async (organizationId: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<{
            listOrganizationAccounts: OrganizationAccountConnection
        }>>({
                query: listOrganizationAccounts,
                variables: {filter: {organizationId}}
            });
        return response.data?.listOrganizationAccounts;
    } catch (error) {
        console.error('Error fetching organization accounts:', error);
        throw error;
    }
};

export const getOrganizationsByAccount = async (accountId: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<{
            listOrganizationAccounts: OrganizationAccountConnection
        }>>({
                query: listOrganizationAccounts,
                variables: {filter: {accountId}}
            });
        return response.data?.listOrganizationAccounts;
    } catch (error) {
        console.error('Error fetching account organizations:', error);
        throw error;
    }
};

export const findAccountByEmail = async (email: string) => {
    try {
        const response = await client.graphql({
                                                  query: listAccountsQuery,
                                                  variables: {filter: {email}}
                                              });
        return (response as { data?: { listAccounts?: { items?: { id: string; email: string }[] } } }).data?.listAccounts?.items?.[0];
    } catch (error) {
        console.error('Error finding account by email:', error);
        throw error;
    }
};



const checkMembershipQuery = `
    query CheckMembership($organizationId: ID!, $accountId: ID!) {
        listOrganizationAccounts(filter: { organizationId: $organizationId, accountId: $accountId }) {
            items {
                id
            }
        }
    }
`;

export const inviteMemberToOrganization = async (organizationId: string, email: string) => {
    try {
        // First check if account already exists
        const existingAccount = await findAccountByEmail(email);

        if (existingAccount) {
            // Check if they're already a member
            const membershipCheck = await client.graphql({
                                                             query: checkMembershipQuery,
                                                             variables: {
                                                                 organizationId,
                                                                 accountId: existingAccount.id
                                                             }
                                                         });

            const isAlreadyMember = ((membershipCheck as { data?: { listOrganizationAccounts?: { items?: unknown[] } } }).data?.listOrganizationAccounts?.items?.length ?? 0) > 0;

            if (isAlreadyMember) {
                throw new Error(`${email} is already a member of this organization`);
            }

            // Account exists but not a member, add them directly
            const response = await client.graphql({
                                                      query: createOrganizationAccountMutation,
                                                      variables: {
                                                          input: {
                                                              organizationId,
                                                              accountId: existingAccount.id,
                                                              organizationAccountStatus: 'ACTIVE'
                                                          }
                                                      }
                                                  });
            return {
                type: 'DIRECT_ADD',
                data: (response as { data?: { createOrganizationAccount?: unknown } }).data?.createOrganizationAccount
            };
        } else {
            // Account doesn't exist, create invitation
            const invitation = {
                id: `inv_${Date.now()}`,
                organizationId,
                email,
                status: 'PENDING',
                created: new Date().toISOString(),
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
            };

            // TODO: Implement actual invitation storage and email sending
            console.log('📧 Invitation email sent to:', email);
            console.log('Invitation details:', invitation);

            return {
                type: 'INVITATION_SENT',
                data: invitation
            };
        }
    } catch (error) {
        console.error('Error inviting member to organization:', error);
        throw error;
    }
};

// Keep the old function for backward compatibility
export const addMemberToOrganization = inviteMemberToOrganization;